import { useSelector } from 'react-redux'
import getSums from '../../../ConceptCalculation/OrganizationUnit/SurveyResults/getSums'
import BarStacked, { GraphValue, Item } from '../../../../../../UI/Chart/BarStacked/BarStacked'
import { Concept, ConceptValues } from '../../../../../../../types/Survey/Analysis/Concept'
import props from '../../../../../../../redux/props'
import { ProductionDistance } from '../../../../../../../types/OrganizationUnit'
import { isUnitAllowedByProductionDistance } from '../../SummaryProductionDistance'
import { isUnitAllowedByFloor } from '../../SummaryFloor'
import { round } from '../../../../../../../utility'

type Props = {
  concept: Concept
  productionDistance?: ProductionDistance
  floorUUID?: string
}

const UnitSquareMeterComparison: React.FC<Props> = ({ concept, productionDistance, floorUUID }) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  const getUnitValues = (unit: ConceptValues, uebergeordneteBedarfe: number = 0): GraphValue[] => {
    const sums = unit ? getSums(unit.ergebnisse) : null
    const items = [
      {
        label: t.survey.analysis.calculation.values.arbeitsplaetze.headline,
        value: round(sums ? sums.einzelarbeit + sums.einzelbueros : 0, 0),
      },
      {
        label: t.survey.analysis.calculation.values.arbeitsorte.headline,
        value: round(
          sums ? sums.kommunikation + sums.kollaboration + unit.ergebnisse.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.practicalQM : 0,
          0,
        ),
      },
      {
        label: t.survey.analysis.calculation.values.sonderbedarfe.headline,
        value: round(sums ? sums.sonderbedarfe : 0, 0),
      },
      {
        label: t.survey.analysis.calculation.values.stauflaeche.headline,
        value: round(sums ? sums.staufleache : 0, 0),
      },
      {
        label: t.survey.analysis.calculation.values.verkehrsflaechen.headline,
        value: round(sums ? sums.verkehrsflaechen : 0, 0),
      },
      {
        label: t.survey.analysis.calculation.values.uebergeordneteBedarfe.headline,
        value: round(uebergeordneteBedarfe, 0),
      },
    ]

    return items
  }

  const getItems = (): Item[] => {
    const uebergeordneteBedarfe = concept.optimization.UEBERGEORDNETE_BEDARFE.reduce((acc, bedarf) => {
      return acc + (!isNaN(bedarf.practicalQM) ? bedarf.practicalQM : 0)
    }, 0)
    const uebergeordneteBedarfeItem = {
      key: 'uebergeordneteBedarfe',
      label: t.survey.analysis.calculation.values.uebergeordneteBedarfe.headline,
      values: getUnitValues(null, uebergeordneteBedarfe),
    }
    const unitUUIDList = Object.keys(concept.units).filter(
      (unitUUID) =>
        unitUUID !== 'sum' &&
        isUnitAllowedByProductionDistance(concept, unitUUID, productionDistance) &&
        isUnitAllowedByFloor(concept, unitUUID, floorUUID),
    )
    return [
      ...unitUUIDList.map((unitUUID) => {
        return {
          key: unitUUID,
          label: concept.units[unitUUID].name,
          values: getUnitValues(concept.units[unitUUID]),
        }
      }),
      !productionDistance ? uebergeordneteBedarfeItem : null,
    ].filter((item) => item !== null)
  }

  if (getItems().length === 0) return null
  return <BarStacked useTransparentColors={false} items={getItems()} suffix={t.generic.squareMeterSuffix} showGrid={true} enableDownload showLegend />
}

export default UnitSquareMeterComparison
